import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Eye, Calendar, Clock, Download } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface PageView {
  id: string;
  page_path: string;
  page_title: string | null;
  timestamp: string;
  ip_address: string;
  user_agent: string;
}

interface PageViewHistoryProps {
  userId: string;
  userName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const PageViewHistory = ({ userId, userName, isOpen, onClose }: PageViewHistoryProps) => {
  const [pageViews, setPageViews] = useState<PageView[]>([]);
  const [loading, setLoading] = useState(true);
  const [oldestTimestamp, setOldestTimestamp] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    console.log('👁️ PageViewHistory component state:', { isOpen, userId, userName });
    
    if (isOpen && userId) {
      console.log('🔍 Fetching page views for user:', userId);
      fetchPageViews();
    }
  }, [isOpen, userId]);

  const fetchPageViews = async () => {
    try {
      setLoading(true);
      
      console.log('📊 Querying page_view_audit table for user_id:', userId);
      
      const { data, error } = await supabase
        .from('page_view_audit')
        .select('*')
        .eq('user_id', userId)
        .order('timestamp', { ascending: false });

      console.log('📦 Query result:', { 
        dataCount: data?.length || 0, 
        error: error ? { message: error.message, code: error.code, details: error.details, hint: error.hint } : null 
      });

      if (error) {
        console.error('❌ Error fetching page views:', error);
        throw error;
      }
      
      const processedData = (data || []).map(view => ({
        ...view,
        ip_address: String(view.ip_address || 'unknown')
      }));
      
      // Find oldest timestamp
      if (processedData.length > 0) {
        const oldest = processedData[processedData.length - 1].timestamp;
        setOldestTimestamp(oldest);
      }
      
      console.log('✅ Processed page views:', processedData);
      setPageViews(processedData);
    } catch (error: any) {
      console.error('❌ Exception in fetchPageViews:', {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        hint: error?.hint,
        stack: error?.stack
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return format(new Date(timestamp), 'PPpp');
  };

  const getPageBadge = (path: string) => {
    if (path.includes('/partner')) {
      return <Badge variant="secondary">Partner Area</Badge>;
    } else if (path.includes('/portfolios')) {
      return <Badge>Portfolio</Badge>;
    } else if (path.includes('/blog') || path.includes('/insights')) {
      return <Badge variant="outline">Content</Badge>;
    }
    return <Badge variant="outline">Public</Badge>;
  };

  const downloadCSV = () => {
    try {
      // Create CSV header
      const headers = ['Timestamp', 'Page Title', 'Page Path', 'IP Address', 'User Agent'];
      
      // Create CSV rows
      const rows = pageViews.map(view => [
        formatTimestamp(view.timestamp),
        view.page_title || 'Untitled',
        view.page_path,
        view.ip_address,
        view.user_agent
      ]);
      
      // Combine header and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `page-views-${userName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Success",
        description: "Page views exported successfully"
      });
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast({
        title: "Error",
        description: "Failed to export page views",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Page View History - {userName}
              </DialogTitle>
              <DialogDescription>
                {oldestTimestamp ? (
                  <>All page views since {format(new Date(oldestTimestamp), 'PPp')} ({formatDistanceToNow(new Date(oldestTimestamp), { addSuffix: true })})</>
                ) : (
                  <>All page views for this user</>
                )}
              </DialogDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCSV}
              disabled={pageViews.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </DialogHeader>
        
        <ScrollArea className="h-[500px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading page views...</div>
            </div>
          ) : pageViews.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">No page views recorded yet</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Timestamp
                    </div>
                  </TableHead>
                  <TableHead>Page</TableHead>
                  <TableHead>Path</TableHead>
                  <TableHead>IP Address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageViews.map((view) => (
                  <TableRow key={view.id}>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {formatTimestamp(view.timestamp)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{view.page_title || 'Untitled'}</div>
                        {getPageBadge(view.page_path)}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {view.page_path}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {view.ip_address}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
